/**
 * Voice-sample lookup for the tutorial / cheat-sheet ▶ Listen buttons.
 *
 * URLs are derived ON THE FLY from the human-readable phrase via a
 * deterministic slug rule that matches the PowerShell generator
 * (`public/voice-samples/_generate.ps1`). So adding a new phrase to a
 * cheat sheet is:
 *
 *   1. Append the phrase to $PHRASES in _generate.ps1 and re-run
 *      (overwrites all .wav files in this folder).
 *   2. Append it to REGISTERED_PHRASES below (or — if you want every
 *      cheat-sheet phrase auto-included — leave it and rely on the
 *      404 falling back to "no speaker shown").
 *
 * Cache-bust: WAVs are served with immutable + 1-year max-age. Bump V
 * whenever the generator runs over existing files so browsers fetch
 * the new content instead of serving stale audio from disk cache.
 */
const BASE = "/voice-samples";
const V = "?v=3";


// Mirror of the PowerShell `To-Slug` function. Lowercase, drop
// punctuation, collapse whitespace, replace spaces with dashes.
function phraseSlug(phrase) {
  if (!phrase) return "";
  return String(phrase)
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\s/g, "-");
}


// Phrases for which we KNOW a WAV exists in /public/voice-samples/.
// Anything not in this set returns null from getVoiceSampleUrl(), so
// the VoicePlayButton stays hidden rather than rendering a broken
// 404 link.
//
// This list mirrors the master $PHRASES array in
// public/voice-samples/_generate.ps1. Re-run the PS script and paste
// the printed list back here whenever phrases change.
const REGISTERED_PHRASES = new Set([
  // Runs + extras
  "dot", "single", "two runs", "three", "four boundary", "four", "boundary",
  "six", "wide", "wide one", "no ball", "leg bye", "bye",
  // Wickets
  "wicket", "out", "caught bowled", "lbw", "run out", "bowled", "stumped",
  "hit wicket", "retired hurt", "mankad",
  // Save / undo / nav
  "save", "undo", "cancel", "next ball",
  "pitch map", "height impact", "wagon wheel",
  // Spatial — pitch fast
  "yorker os", "good length ms", "back of a length oo", "bouncer wdl",
  "full toss ls", "beamer ms",
  // Spatial — pitch long
  "yorker outside off", "good length on middle", "back of a length leg stump",
  "short outside leg", "full toss on the stumps", "half tracker outside off",
  // Spatial — impact fast
  "impact chest ms", "impact head oo", "impact knee ls", "impact waist ms",
  "impact boot ms", "impact thigh oo",
  // Spatial — impact long
  "chest high on middle", "head high outside off", "shoulder high leg stump",
  "waist high middle", "knee roll outside off", "shin high leg stump",
  // Wagon
  "mid off", "mid on", "cover", "deep cover", "extra cover",
  "sweeper cover", "point", "deep point", "third man",
  "fine leg", "deep fine leg", "square leg", "deep square leg",
  "mid wicket", "deep mid wicket", "cow corner",
  "long off", "long on", "wide long on",
  "slip", "gully", "leg slip",
  "played to deep cover", "hit to mid wicket", "towards long on",
  // Keeper
  "standard", "up to stumps", "leg side shift", "off side shift",
  // Bounce
  "extra bounce", "kept low", "variable bounce",
  // Delivery pace
  "in swinger", "out swinger", "off cutter", "leg cutter",
  "slower ball", "knuckle ball", "seam in", "seam away",
  "angled in", "angled away", "back of hand",
  // Delivery spin
  "off break", "leg spinner", "googly", "doosra", "carrom ball",
  "arm ball", "top spinner", "slider", "flipper", "quicker ball",
  "left arm orthodox",
  // Spin direction
  "off spin", "leg spin", "top spin", "wrong un", "carrom",
  // Drift
  "drift in", "drift away", "no drift",
  // Turn
  "big turn", "small turn", "straight on", "skid",
  // Stroke
  "cover drive", "straight drive", "off drive", "on drive", "square drive",
  "pull", "hook", "sweep", "reverse sweep", "slog sweep", "paddle sweep",
  "scoop", "cut", "late cut", "upper cut", "flick", "glance", "switch hit",
  "forward defence", "backward defence", "leave", "padded",
  // Foot movement
  "front foot", "back foot", "down the track", "backs away",
  "swayed away", "ducked", "deep in crease", "no movement",
  // Shot connection
  "middled", "well timed", "mis timed", "outside edge", "inside edge",
  "top edge", "leading edge", "thick edge", "play and miss", "beaten",
  "shoulder arms", "bat pad", "gloved", "hit pad", "hit body",
  // Air movement
  "along the ground", "flat", "loopy", "high in the air",
  // Control
  "controlled", "uncontrolled", "in control", "lost control",
  // Scope-locked
  "stroke cover drive", "delivery slider", "pitch yorker outside off",
  "impact chest middle stump", "wagon deep cover", "keeper up to stumps",
  "bounce kept low",
  // Quick / short / Hindi
  "quick run", "short run", "chauka", "chakka", "vikit",
]);


// Phrase-text normalisation so caller-supplied variants match. Strips
// surrounding quotes/whitespace and lowercases. (The slug rule above
// drops punctuation too, but the membership check uses the raw phrase
// string.)
function normalize(phrase) {
  if (!phrase) return "";
  return String(phrase).trim().replace(/^["']|["']$/g, "").toLowerCase();
}


export function hasVoiceSample(phrase) {
  const n = normalize(phrase);
  if (REGISTERED_PHRASES.has(n)) return true;
  // Fallback: maybe the cheat sheet has a punctuation-only variant
  // (e.g. "mis-timed" vs "mis timed"). Try matching by slug.
  const slug = phraseSlug(n);
  for (const p of REGISTERED_PHRASES) {
    if (phraseSlug(p) === slug) return true;
  }
  return false;
}

export function getVoiceSampleUrl(phrase) {
  if (!hasVoiceSample(phrase)) return null;
  // Always derive URL from the slug — handles both registered phrases
  // and punctuation variants in one path.
  return `${BASE}/${phraseSlug(normalize(phrase))}.wav${V}`;
}


// Singleton Audio so a new click stops the previous playback — no
// overlapping samples when an operator taps rapidly.
let _audio = null;

// 2026-05-27 founder ask: voice samples should NEVER make sound on
// mobile / tablet — operators were getting surprised by audio when
// tapping into the BBP coding screen. Desktop (lg+ = 1024px) keeps
// the existing behaviour so the cheat-sheet's 🔊 buttons still work.
function audioAllowed() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  try { return window.matchMedia("(min-width: 1024px)").matches; }
  catch { return false; }
}

export function playVoiceSample(phrase) {
  if (!audioAllowed()) return false;
  const u = getVoiceSampleUrl(phrase);
  if (!u) return false;
  try {
    if (_audio) {
      _audio.pause();
      _audio.currentTime = 0;
    }
    _audio = new Audio(u);
    _audio.play().catch(() => {});
    return true;
  } catch {
    return false;
  }
}
